import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Trash2, ShoppingBag, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import { ProductImage } from "@/components/shop/ProductImage";

const formatXOF = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(n) || 0) + " F";

export default function Panier() {
  const { user, cart, updateCartItem, removeCartItem } = useShop();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);

  if (!user) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-20 text-center">
        <ShoppingBag className="h-10 w-10 mx-auto text-[#16241B]/30" />
        <h1 className="mt-6 font-display text-3xl text-[#16241B]">Connectez-vous pour voir votre panier</h1>
        <p className="mt-3 text-[#16241B]/70">
          Votre panier Zafara est synchronisé entre le site et l'application mobile.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/compte/connexion?next=/panier" className="rounded-full bg-[#0E7A3B] text-white px-6 py-3 font-semibold hover:bg-[#0d6a34] transition-colors">
            Se connecter
          </Link>
          <Link to="/compte/inscription?next=/panier" className="rounded-full border border-[#EDE7D9] bg-white text-[#16241B] px-6 py-3 font-semibold hover:border-[#0E7A3B] transition-colors">
            Créer un compte
          </Link>
        </div>
      </section>
    );
  }

  const items = cart.items || [];
  const subtotal = items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);

  const change = async (id, q) => {
    if (q < 1) return;
    setBusyId(id);
    try { await updateCartItem(id, q); }
    catch (e) { toast.error(e.message || "Erreur"); }
    finally { setBusyId(null); }
  };
  const remove = async (id) => {
    setBusyId(id);
    try { await removeCartItem(id); toast.success("Article retiré."); }
    catch (e) { toast.error(e.message || "Erreur"); }
    finally { setBusyId(null); }
  };

  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-10 md:py-16">
      <Helmet>
        <title>Panier — Zafara Bénin</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <h1 className="font-display text-4xl md:text-5xl text-[#16241B] tracking-tighter">
        Votre <span className="text-[#0E7A3B]">panier</span>
      </h1>
      <p className="mt-2 text-[#16241B]/70">{items.length} article{items.length > 1 ? "s" : ""}</p>

      {items.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-dashed border-[#EDE7D9] p-12 text-center">
          <ShoppingBag className="h-10 w-10 mx-auto text-[#16241B]/30" />
          <p className="mt-4 text-[#16241B]/70">Votre panier est vide.</p>
          <Link to="/catalogue" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3 font-semibold hover:bg-[#0E7A3B] hover:text-white transition-colors">
            Explorer le catalogue <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`cart-item-${it.id}`}
                className="rounded-3xl border border-[#EDE7D9] bg-white p-4 flex gap-4 items-start"
              >
                <div className="h-24 w-24 shrink-0 rounded-2xl overflow-hidden bg-[#FBFAF6] border border-[#EDE7D9]">
                  <ProductImage src={it.image_url} alt={it.name} className="h-full w-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-[#16241B] leading-tight line-clamp-2">{it.name || it.product_name}</h3>
                  {it.size && <p className="mt-1 text-xs text-[#16241B]/60">Taille : {it.size}</p>}
                  <p className="mt-2 font-display font-bold text-[#0E7A3B]">{formatXOF(it.price)}</p>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <div className="inline-flex items-center rounded-full border border-[#EDE7D9]">
                    <button
                      data-testid={`cart-dec-${it.id}`}
                      disabled={busyId === it.id}
                      onClick={() => change(it.id, it.quantity - 1)}
                      className="px-3 py-1.5 hover:bg-[#FBFAF6]"
                    >−</button>
                    <span className="px-3 text-sm font-semibold">{it.quantity}</span>
                    <button
                      data-testid={`cart-inc-${it.id}`}
                      disabled={busyId === it.id}
                      onClick={() => change(it.id, it.quantity + 1)}
                      className="px-3 py-1.5 hover:bg-[#FBFAF6]"
                    >+</button>
                  </div>
                  <button
                    data-testid={`cart-remove-${it.id}`}
                    disabled={busyId === it.id}
                    onClick={() => remove(it.id)}
                    className="inline-flex items-center gap-1 text-xs text-[#E8112D] hover:underline"
                  >
                    {busyId === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Retirer
                  </button>
                </div>
              </div>
            ))}
          </div>

          <aside className="rounded-3xl border border-[#EDE7D9] bg-white p-6 h-fit sticky top-24">
            <h2 className="font-display text-xl text-[#16241B]">Récapitulatif</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#16241B]/70">Sous-total</span>
                <span data-testid="cart-subtotal" className="font-semibold">{formatXOF(subtotal)}</span>
              </div>
              <div className="flex justify-between text-[#16241B]/60 text-xs">
                <span>Livraison</span>
                <span>Calculée à l'étape suivante</span>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-[#EDE7D9] flex justify-between items-baseline">
              <span className="font-display text-lg text-[#16241B]">Total</span>
              <span data-testid="cart-total" className="font-display font-bold text-2xl text-[#0E7A3B]">{formatXOF(cart.total || subtotal)}</span>
            </div>
            <button
              data-testid="cart-checkout"
              onClick={() => navigate("/commande")}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3.5 font-display font-bold shadow-[0_15px_40px_rgba(245,168,0,0.35)] hover:bg-[#0E7A3B] hover:text-white transition-all"
            >
              Passer commande <ArrowRight className="h-4 w-4" />
            </button>
          </aside>
        </div>
      )}
    </section>
  );
}
